import cloneDeep from 'lodash.clonedeep'

const modulePermissions = {
  campaign: 'campaigns', sellerOffer: 'campaigns', creative: 'campaigns',
  ringTreeTargetRequest: 'campaigns', sellerWebhooks: 'campaigns',
  ringbaTokens: 'admin', ringbaAccounts: 'admin',
  callLogs: 'call_logs', insights: 'call_logs'
}
const billingActions = {
  getBuyerAccountBalance: null, getAccountBillingSummary: null, getAccountPayments: null,
  fetchPurchasedCalls: 'call_logs',
  getBankInfo: 'billing', getUnmaskedBankInfo: 'billing', saveBankInfo: 'billing',
  updateAutoRechargeSettings: 'billing',
  revokeBillingData: null
}
const accountActions = {
  getAccountUsers: 'admin', createAccountUser: 'admin', removeAccountUser: 'admin',
  setAccountUserPermissions: 'admin',
  updateBuyerAccountBudgetCap: 'campaigns', updateBuyerCreativeSettings: 'campaigns',
  updateAccountTimezone: 'campaigns', setRingbaAccount: 'admin'
}
const dashboardBillingActions = [
  'getSystemAccountBankDetails', 'createAdvancePaymentRequest', 'getSellerPayouts', 'getCreditCardCharges', 'getAdvancePayRequests'
]
// Every member sees balances and invoices. Cards, payments, history and advance pay need billing.
const dashboardOpenActions = ['generateInvoiceDownloadLink']

export function permissionForAction(module, action) {
  if (module === 'callLogs' && action === 'getBuyerBlockedChannels') return ['call_logs', 'campaigns']
  if (module === 'callLogs' && action === 'unblockChannel') return 'campaigns'
  if (modulePermissions[module]) return modulePermissions[module]
  if (module === 'billing') return action in billingActions ? billingActions[action] : 'billing'
  if (module === 'account') return accountActions[action]
  if (module === 'dashboard') {
    if (dashboardOpenActions.includes(action)) return undefined
    if (dashboardBillingActions.includes(action)) return 'billing'
    if (['getBuyerDashboardData', 'getSellerDashboardData'].includes(action)) return 'call_logs'
    return 'campaigns'
  }
}

export function accessSignature(state, getters) {
  return JSON.stringify([
    state.auth?.currentAccountDetails?.id || state.auth?.userDetails?.lastAccessedAccountId,
    [...(getters['auth/accountPermissions'] || [])].sort(),
    state.accountAccessEpoch || 0
  ])
}

// Stale timers/cached dispatches must not fetch after a grant is revoked. Late responses must not
// repopulate state belonging to the previous account or permission set.
export function guardAccountModules(modules) {
  Object.entries(modules).forEach(([name, module]) => {
    Object.entries(module.actions || {}).forEach(([action, handler]) => {
      const required = permissionForAction(name, action)
      if (!required) return
      module.actions[action] = async (context, payload) => {
        const permissions = Array.isArray(required) ? required : [required]
        if (!permissions.some(permission => context.rootGetters['auth/hasPermission'](permission))) return null
        const signature = accessSignature(context.rootState, context.rootGetters)
        const stillAllowed = () => signature === accessSignature(context.rootState, context.rootGetters)
        const guarded = {
          ...context,
          commit: (...args) => { if (stillAllowed()) context.commit(...args) },
          dispatch: (...args) => stillAllowed() ? context.dispatch(...args) : Promise.resolve(null)
        }
        const result = await handler(guarded, payload)
        return stillAllowed() ? result : null
      }
    })
  })
  return modules
}

export function createAccountAccessPlugin(modules) {
  const names = [...Object.keys(modulePermissions), 'billing', 'dashboard', 'account']
  const defaults = Object.fromEntries(names.filter(name => modules[name]).map(name =>
    [name, cloneDeep(typeof modules[name].state === 'function' ? modules[name].state() : modules[name].state)]))
  return store => {
    store.watch(
      (state, getters) => JSON.stringify([
        state.auth?.currentAccountDetails?.id || state.auth?.userDetails?.lastAccessedAccountId,
        [...(getters['auth/accountPermissions'] || [])].sort()
      ]),
      (current, previous) => {
        store.cache?.clear()
        store.commit('INVALIDATE_ACCOUNT_ACCESS', defaults)
        store.dispatch('hideDashboardNotification')
        const [accountId, granted] = JSON.parse(current)
        const [oldAccountId, oldGranted] = JSON.parse(previous)
        if (accountId !== oldAccountId || (oldGranted.includes('campaigns') && !granted.includes('campaigns'))) {
          store.dispatch('softphone/teardownSoftphone', { isUnloading: true, skipServerPause: true })
        }
      },
      { sync: true }
    )
  }
}
